import { useState, useEffect, useRef, useMemo } from "react";
import { ValidatedFormField } from "@/admin/settings/customer/dealer/components/ValidatedFormField";
import { PasswordInput } from "@/components/ui/password-input";
import {
    validateName,
    validateEmail,
    validatePhone,
    validatePasswordConfirmation,
    validatePassword,
} from "@/utils/formValidation";
import { authLabels } from "@/i18n";

export interface PersonalInfoData {
    username: string;
    email: string;
    phoneNumber: string;
    password: string;
    confirmPassword: string;
}

interface PersonalInfoSectionProps {
    value: PersonalInfoData;
    onChange: (data: PersonalInfoData, valid: boolean) => void;
    /** When true, name is taken from BankID claims — hide name field. */
    nameFromBankId?: boolean;
}

export const PersonalInfoSection = ({
    value,
    onChange,
    nameFromBankId = false,
}: PersonalInfoSectionProps) => {
    const authLabels_i18n = authLabels();
    const [local, setLocal] = useState(value);
    const isUpdatingFromProps = useRef(false);

    useEffect(() => {
        if (
            value.username !== local.username ||
            value.email !== local.email ||
            value.phoneNumber !== local.phoneNumber ||
            value.password !== local.password ||
            value.confirmPassword !== local.confirmPassword
        ) {
            isUpdatingFromProps.current = true;
            setLocal(value);
            isUpdatingFromProps.current = false;
        }
    }, [value]);

    const { isValid } = useMemo(() => {
        const errors = {
            username: nameFromBankId ? [] : validateName(local.username).errors,
            email: validateEmail(local.email).errors,
            phoneNumber: validatePhone(local.phoneNumber).errors,
            password: validatePassword(local.password).errors,
            confirmPassword: validatePasswordConfirmation(local.password, local.confirmPassword).errors,
        };

        const isValid = Object.values(errors).every((arr) => arr.length === 0);

        return { errors, isValid };
    }, [
        nameFromBankId,
        local.username,
        local.email,
        local.phoneNumber,
        local.password,
        local.confirmPassword,
    ]);

    useEffect(() => {
        if (!isUpdatingFromProps.current) {
            onChange(local, isValid);
        }
    }, [local, isValid, onChange]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!nameFromBankId ? (
                    <ValidatedFormField
                        id="register-username"
                        label={authLabels_i18n.forms.fields.name}
                        placeholder={authLabels_i18n.forms.fields.namePlaceholder}
                        required
                        value={local.username}
                        onChange={(v) => setLocal((prev) => ({ ...prev, username: v }))}
                        validator={validateName}
                    />
                ) : null}
                <ValidatedFormField
                    id="register-email"
                    label={authLabels_i18n.forms.fields.email}
                    placeholder={authLabels_i18n.forms.fields.emailPlaceholder}
                    type="email"
                    required
                    value={local.email}
                    onChange={(v) => setLocal((prev) => ({ ...prev, email: v }))}
                    validator={validateEmail}
                />
                {nameFromBankId ? (
                    <ValidatedFormField
                        id="register-phoneNumber"
                        label={authLabels_i18n.forms.fields.phone}
                        placeholder={authLabels_i18n.forms.fields.phonePlaceholder}
                        required
                        value={local.phoneNumber}
                        onChange={(v) => setLocal((prev) => ({ ...prev, phoneNumber: v }))}
                        validator={validatePhone}
                    />
                ) : null}
            </div>
            {!nameFromBankId ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ValidatedFormField
                        id="register-phoneNumber"
                        label={authLabels_i18n.forms.fields.phone}
                        placeholder={authLabels_i18n.forms.fields.phonePlaceholder}
                        required
                        value={local.phoneNumber}
                        onChange={(v) => setLocal((prev) => ({ ...prev, phoneNumber: v }))}
                        validator={validatePhone}
                    />
                </div>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PasswordInput
                    id="register-password"
                    label={authLabels_i18n.forms.fields.password}
                    value={local.password}
                    onChange={(v) => setLocal((prev) => ({ ...prev, password: v }))}
                    required
                    autoComplete="new-password"
                />
                <PasswordInput
                    id="register-confirmPassword"
                    label={authLabels_i18n.forms.fields.confirmPassword}
                    value={local.confirmPassword}
                    onChange={(v) => setLocal((prev) => ({ ...prev, confirmPassword: v }))}
                    required
                    showRequirements={false}
                    autoComplete="new-password"
                    validator={(value) => validatePasswordConfirmation(local.password, value)}
                />
            </div>
        </div>
    );
};
